import axios from 'axios';
import type {
  WorkOrdersProgressCallback,
} from '../types/apiTypes';
import type {
  CycleCountDetails,
  WorkOrderItem,
  WorkOrdersResponse,
  RecentAlertItem,
  RecentCountHistoryItem,
  BundleLocationRow,
} from '../schemas/apiResponse.schemas';
import {
  WorkOrdersResponseSchema,
  CycleCountDetailsSchema,
  RecentAlertItemSchema,
  RecentCountHistoryItemSchema,
  BundleLocationRowSchema,
  SubmitCycleCountResponseSchema,
  WorkOrderItemSchema,
} from '../schemas/apiResponse.schemas';
import { normalizeSku } from '../utils/normalizeSku';
import { validateResponse } from './validateResponse';

const API_BASE = '/api/inventory-counts';

export const fetchWorkOrders = async (): Promise<WorkOrdersResponse> => {
  try {
    const response = await axios.get(`${API_BASE}/work-orders`);
    return validateResponse(WorkOrdersResponseSchema, response.data, 'fetchWorkOrders');
  } catch (error: unknown) {
    console.error('Error fetching work orders:', error);
    throw error;
  }
};

/** Fetch work orders with streaming progress. Calls onProgress(message) for each log line. */
export const fetchWorkOrdersStream = (onProgress: WorkOrdersProgressCallback): Promise<WorkOrdersResponse> => {
  return new Promise((resolve, reject) => {
    let settled = false;
    const finish = <T,>(fn: (arg: T) => void, arg: T): void => {
      if (settled) return;
      settled = true;
      es.close();
      fn(arg);
    };

    const url = `${API_BASE}/work-orders-stream`;
    const base = window.location.origin;
    const fullUrl = url.startsWith('http') ? url : `${base}${url.startsWith('/') ? '' : '/'}${url}`;
    const es = new EventSource(fullUrl);

    // EventSource.addEventListener types custom events as Event, but named SSE events
    // are always MessageEvent at runtime. TypeScript's DOM lib lacks an overload for this.
    es.addEventListener('progress', (e) => {
      try {
        const me = e as MessageEvent<string>;
        const data: unknown = JSON.parse(me.data);
        if (data && typeof data === 'object' && 'message' in data && typeof (data as { message: unknown }).message === 'string' && typeof onProgress === 'function') {
          onProgress((data as { message: string }).message);
        }
      } catch (_) {}
    });

    es.addEventListener('result', (e) => {
      try {
        const me = e as MessageEvent<string>;
        const result: unknown = JSON.parse(me.data);
        finish(resolve, result as WorkOrdersResponse);
      } catch (err) {
        finish(reject, err);
      }
    });

    es.addEventListener('error', (e: Event) => {
      const me = e as MessageEvent<string>;
      const msg = me.data ? (() => { try { const d = JSON.parse(me.data) as { error?: string }; return d.error; } catch (_) { return null; } })() : null;
      finish(reject, new Error(msg || 'Connection lost'));
    });
  });
};

export const fetchCycleCountDetails = async (sku: string): Promise<CycleCountDetails> => {
  try {
    const response = await axios.get(`${API_BASE}/cycle-count-details/${encodeURIComponent(sku)}`);
    return validateResponse(CycleCountDetailsSchema, response.data, 'fetchCycleCountDetails');
  } catch (error: unknown) {
    console.error('Error fetching cycle count details:', error);
    throw error;
  }
};

export const fetchRecentAlerts = async (): Promise<RecentAlertItem[]> => {
  try {
    const response = await axios.get(`${API_BASE}/recent-alerts`);
    const alerts = response.data?.alerts || [];
    return validateResponse(RecentAlertItemSchema.array(), alerts, 'fetchRecentAlerts');
  } catch (error: unknown) {
    console.error('Error fetching recent alerts:', error);
    throw error;
  }
};

export const fetchRecentCountsBySku = async (sku: string): Promise<RecentCountHistoryItem[]> => {
  try {
    const response = await axios.get(`${API_BASE}/recent-counts/${encodeURIComponent(sku)}`);
    const counts = response.data?.counts || [];
    return validateResponse(RecentCountHistoryItemSchema.array(), counts, 'fetchRecentCountsBySku');
  } catch (error: unknown) {
    console.error('Error fetching recent count history:', error);
    throw error;
  }
};

export const fetchBundleLocationsByComponentSku = async (sku: string): Promise<BundleLocationRow[]> => {
  try {
    const response = await axios.get(`${API_BASE}/bundle-locations/${encodeURIComponent(sku)}`);
    const locations = response.data?.locations || [];
    return validateResponse(BundleLocationRowSchema.array(), locations, 'fetchBundleLocationsByComponentSku');
  } catch (error: unknown) {
    console.error('Error fetching bundle locations:', error);
    throw error;
  }
};

export const submitCycleCount = async (id: number | string, actualInventory: number): Promise<{ success: boolean; data: WorkOrderItem; shopifyUpdated: boolean }> => {
  try {
    const response = await axios.post(`${API_BASE}/complete-count`, {
      id,
      actualInventory,
    });
    return validateResponse(SubmitCycleCountResponseSchema, response.data, 'submitCycleCount');
  } catch (error: unknown) {
    console.error('Error submitting cycle count:', error);
    throw error;
  }
};

/** Create a manual count request for a SKU. Backend gets Shopify inventory, inserts cycle_count_events MANUAL, sets eligibility false. */
export const createManualCountRequest = async (sku: string): Promise<WorkOrderItem> => {
  try {
    const response = await axios.post(`${API_BASE}/create-manual-count`, { sku: sku.trim() });
    return validateResponse(WorkOrderItemSchema, response.data, 'createManualCountRequest');
  } catch (error: unknown) {
    console.error('Error creating manual count:', error);
    throw error;
  }
};

const variantImageCache = new Map<string, string | null>();
const variantImageInflight = new Map<string, Promise<string | null>>();
const warmedVariantImages = new Map<string, HTMLImageElement>();

/** Cached variant image URL, or `undefined` when this SKU has not been resolved yet. */
export function readCachedVariantImage(sku: string): string | null | undefined {
  const key = normalizeSku(sku);
  if (!key || !variantImageCache.has(key)) return undefined;
  return variantImageCache.get(key) ?? null;
}

function rememberVariantImage(key: string, url: string | null): void {
  variantImageCache.set(key, url);
  if (!url || warmedVariantImages.has(key)) return;
  const image = new Image();
  image.src = url;
  warmedVariantImages.set(key, image);
}

/** Resolve component thumbnails ahead of expand so uncollapse does not hit the network again. */
export function preloadVariantImages(skus: readonly string[]): void {
  for (const sku of skus) {
    void getVariantImage(sku);
  }
}

export const getVariantImage = async (sku: string): Promise<string | null> => {
  const key = normalizeSku(sku);
  if (!key) return null;
  if (variantImageCache.has(key)) {
    return variantImageCache.get(key) ?? null;
  }
  const pending = variantImageInflight.get(key);
  if (pending) return pending;

  const request = (async (): Promise<string | null> => {
    try {
      const response = await axios.get(`${API_BASE}/variant-image/${encodeURIComponent(key)}`);
      const imageUrl: unknown = response.data?.imageUrl;
      const url = typeof imageUrl === 'string' && imageUrl.trim() !== '' ? imageUrl : null;
      rememberVariantImage(key, url);
      return url;
    } catch (error: unknown) {
      console.error('Error fetching variant image:', error);
      return null;
    } finally {
      variantImageInflight.delete(key);
    }
  })();

  variantImageInflight.set(key, request);
  return request;
};
